import { useState, useEffect } from "react";
import { Table, Icon, Header, Divider, Progress } from "semantic-ui-react";
import { useLazyQuery, useMutation } from "@apollo/client";
import { FileUploader } from "react-drag-drop-files";
import { FILES, CREATE_FILE, GET_UPLOAD_LINK } from "../graphQueries";
import axios from "axios";
import moment from "moment";
import { toast } from "react-toastify";

function sanitizeS3Filename(filename) {
  return filename
    .replace(/[^a-zA-Z0-9.\-_]/g, "_")
    .replace(/_{2,}/g, "_")
    .replace(/^_|_$/g, "");
}

const JobDocuments = ({ selectedJob, loginContext, getDownloadUrl }) => {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Get all documents for this job
  const [getFiles] = useLazyQuery(FILES, {
    fetchPolicy: "network-only",
    variables: {
      jobId: selectedJob.jobId,
      type: "job_documents",
    },
    onCompleted: (res) => setFiles(res?.files || []),
    onError: (err) => toast.error(err.message),
  });

  useEffect(() => {
    if (selectedJob.jobId) getFiles();
    // eslint-disable-next-line
  }, [selectedJob.jobId]);

  const [getUploadLink] = useLazyQuery(GET_UPLOAD_LINK, {
    fetchPolicy: "network-only",
  });
  const [createFile] = useMutation(CREATE_FILE, {
    onCompleted: () => {
      toast.success("File uploaded!");
      getFiles();
    },
    onError: (error) => toast.error(error.message),
  });

  // --- File upload handler
  const handleFileChange = async (file) => {
    if (!file) return;
    setUploading(true);
    setUploadProgress(0);

    try {
      const filename = sanitizeS3Filename(file.name);
      const path = `documents/${selectedJob.jobId}/${filename}`;
      const { data } = await getUploadLink({ variables: { file: path } });
      if (!data?.getUploadLink?.link)
        throw new Error("No upload link returned.");

      await axios.put(data.getUploadLink.link, file, {
        headers: { "Content-Type": file.type },
        onUploadProgress: (progressEvent) => {
          const percent = progressEvent.total
            ? progressEvent.loaded / progressEvent.total
            : 0;
          setUploadProgress(percent);
        },
      });

      await createFile({
        variables: {
          userId: selectedJob.userId,
          jobId: selectedJob.jobId,
          type: "job_documents",
          path,
          filename,
        },
      });

      setUploadProgress(0);
    } catch (err) {
      toast.error(err.message || "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  // --- Download
  const handleDownload = async (file) => {
    try {
      const url = await getDownloadUrl(file.path);
      if (!url) throw new Error("No download URL");
      const res = await fetch(url);
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = file.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 1000);
    } catch (e) {
      toast.error("Download failed: " + (e?.message || "unknown"));
    }
  };

  return (
    <div>
      <Header
        content="Upload Documents"
        subheader="Attach any files related to this job (PDF, Word, images, etc.)"
      />
      <FileUploader
        handleChange={handleFileChange}
        name="file"
        types={["PDF", "DOC", "DOCX", "PNG", "JPG", "JPEG", "TXT"]}
        hoverTitle=" "
        children={
          <div
            style={{
              cursor: "pointer",
              borderStyle: "dashed",
              flex: 1,
              width: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              borderRadius: "10px",
              borderColor: "#888888",
              padding: "20px",
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                width: "100%",
              }}
            >
              <Header
                content={
                  uploading ? "Uploading..." : "Drop a file here to upload"
                }
                subheader="Common file types supported"
              />
              {uploading && (
                <Progress
                  style={{ width: "100%" }}
                  fluid
                  indicating={uploading}
                  percent={Math.ceil(uploadProgress * 100)}
                  progress={Math.ceil(uploadProgress * 100)}
                />
              )}
            </div>
          </div>
        }
      />

      <Divider />

      <Header content="Uploaded Documents" />
      <Table compact size="small" style={{ marginTop: 10 }}>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>Filename</Table.HeaderCell>
            <Table.HeaderCell>Uploaded</Table.HeaderCell>
            <Table.HeaderCell>Action</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {files.map((file) => (
            <Table.Row key={file.fileId}>
              <Table.Cell>{file.filename}</Table.Cell>
              <Table.Cell>
                {file.createdAt ? moment(file.createdAt).format("LLL") : ""}
              </Table.Cell>
              <Table.Cell>
                <Icon
                  name="download"
                  link
                  onClick={() => handleDownload(file)}
                  title="Download"
                />
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
      {files.length === 0 && (
        <div style={{ color: "#888", marginTop: 10 }}>
          No documents uploaded yet.
        </div>
      )}
    </div>
  );
};

export default JobDocuments;
